import { Controller } from "@hotwired/stimulus";

// Supplier RFX response form (#36899, #37313). Handles the pricing-table
// interactions: marking a row N/A grays it out and disables its price input. The
// Submit button stays disabled until every required line item is priced or N/A,
// every priced line also carries a list price, AND both agreement
// acknowledgments are checked. "Save for later" has no target here, so it is
// never disabled — a partial draft always saves.
//
// RFI completeness is enforced by Alpaca's own validation on submit (the form's
// submit_button_selector handler), and the server re-checks pricing + duration +
// acknowledgments in Rfx::Provider::Submit — this is a progressive-enhancement
// UX layer.
export default class extends Controller {
  static targets = ["submit", "listPrice", "negotiatedPrice", "row", "acknowledgment", "notes", "notesDialog"];

  connect() {
    this.rowTargets.forEach((row) => this.applyRowState(row));
    this.refresh();
  }

  toggleRow(event) {
    const row = event.target.closest(".rfx-pricing-row");
    if (row) this.applyRowState(row);
    this.refresh();
  }

  // Fill a line's price input from a prior-price "Use" button (#38260). The
  // button lives in the expandable picker card below the pricing row, so the
  // target input is resolved by id (the `input` param) rather than by DOM
  // proximity. Fires the same `input` event a keystroke would, so submit-gating
  // (refresh) reacts. On-demand only — never pre-fills or overwrites silently.
  fillPrice(event) {
    const price = document.getElementById(event.params.input);
    if (!price || price.disabled) return;

    price.value = event.params.price;
    price.dispatchEvent(new Event("input", { bubbles: true }));
  }

  // Asked once, before submitting, when the supplier has priced something and
  // differentiated nothing (#40342). One note stands for the whole response — a
  // supplier who has explained one line has understood the field, so there is
  // nothing left to prompt them about. Advisory only: the dialog's own button is a
  // plain submit and Rfx::Provider::Submit carries no notes rule, so this can never
  // block a submission. The import side asks the same question
  // (LineItemHandler#notes_absent?) — keep the two in step.
  //
  // Bound to the form's submit rather than the button's click, so the RFI's own
  // validator gets the last word: Alpaca cancels the click on an invalid form
  // (_alpaca_form_init.html.haml), and a click listener here would still fire and
  // stack this dialog on top of its alert. Only "Submit Response" is prompted —
  // "Save for later" is formnovalidate, the dialog's "Submit anyway" must not
  // re-prompt, and implicit submission (Enter) names the first submit button,
  // which is the draft.
  //
  // stopImmediatePropagation is load-bearing: preventDoubleSubmission
  // (shared/forms.js, applied to every form by shared_bs5.js) latches
  // data("submitted") on the first submit event and cancels every later one. A
  // bare preventDefault still lets it see this cancelled event, which then
  // swallows whatever the supplier does next — submitting after adding a note,
  // or saving a draft. Stopping here means the latch is never set, so no
  // dismissal path (the dialog's own buttons, backdrop, or Escape) has anything
  // to undo, and real submits keep their double-submit protection.
  confirmNotes(event) {
    if (!event.submitter?.classList.contains("rfx-form-submit")) return;
    if (!this.notesAbsent()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.notesDialogTarget.showModal();
  }

  // A response with nothing priced owes no note, so it is never prompted.
  notesAbsent() {
    if (!this.rowTargets.some((row) => this.quoted(row))) return false;

    return this.rowTargets.every((row) => (this.notesFor(row)?.value ?? "").trim() === "");
  }

  notApplicable(row) {
    return Boolean(row.querySelector(".rfx-na-checkbox")?.checked);
  }

  // A row is quoted when it carries a positive negotiated price and is not N/A.
  // Both submit-time rules exempt the rest: an unquoted optional line never
  // launches, and an N/A line is a statement in itself.
  quoted(row) {
    return !this.notApplicable(row) && this.isPositive(row.querySelector("[data-rfx-response-target~='negotiatedPrice']"));
  }

  notesFor(row) {
    return row.querySelector("[data-rfx-response-target~='notes']");
  }

  applyRowState(row) {
    // A row has up to four price inputs (list + Scientist.com + negotiated,
    // #37935, plus previous price on the lines that are asked, #40129); grey out
    // and disable them all when N/A is checked. Only the two that gate submit
    // carry targets — the class is what marks a row's price inputs.
    const prices = row.querySelectorAll(".rfx-price-input");
    if (prices.length === 0) return;

    const disabled = this.notApplicable(row);
    row.classList.toggle("rfx-pricing-row--na", disabled);
    prices.forEach((price) => {
      price.disabled = disabled;
    });
  }

  refresh() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = !(this.pricingComplete() && this.acknowledgmentsComplete());
    }
  }

  // The client-side mirror of Rfx::Provider::Submit#pricing_errors, gated per ROW
  // rather than per input, because the two rules read different columns: a
  // required row must carry a negotiated price, and any row that carries one must
  // also carry a list price (#37935). An unquoted optional row is fine — it never
  // launches. Keep this in step with the server gate and EditPresenter#pricing_complete?.
  pricingComplete() {
    return this.rowTargets.every((row) => {
      if (this.notApplicable(row)) return true;

      const quoted = this.quoted(row);
      const listed = this.isPositive(row.querySelector("[data-rfx-response-target~='listPrice']"));

      if (row.dataset.required && !quoted) return false;

      return !quoted || listed;
    });
  }

  isPositive(input) {
    return Boolean(input) && parseFloat(input.value) > 0;
  }

  acknowledgmentsComplete() {
    return this.acknowledgmentTargets.every((checkbox) => checkbox.checked);
  }
}
